<template>
  <AppWithLoading :fetch="fetchData" v-slot:default="{loader, localLoading}">
    <div class="payment-methods">
      <Preloader v-if="renewLoading" />
      <template v-else>
        <InactiveTariffAlert
          v-if="isExpired && !hasFailedRenewal"
          class="mb-5"
          @renew-start="renewLoading = true"
          @renew-end="renewLoading = false"
        />
        <ProlongSubscription
          v-if="isExpired && hasFailedRenewal"
          class="mb-5"
          @renew-start="renewLoading = true"
          @renew-end="renewLoading = false"
        />
        <template v-if="!localLoading">
          <h2 class="payment-methods__title">
            {{ $t('title') }}
          </h2>
          <div v-if="account.driver === paymentDrivers.ALFABANK" class="payment-methods__subtitle mb-6">
            {{ $t('subtitle') }}
          </div>
          <div v-if="paymentMethods.length">
            <PaymentMethodsListItem
              :method="method"
              :opened-id="openedId"
              :key="index"
              v-for="(method, index) in paymentMethods"
              @fetchData="loader(() => fetchData())"
              @edit="changeOpenedId"
            />
            <PaymentMethodCardAlfabankAdd class="mb-10" v-if="account.driver === paymentDrivers.ALFABANK" />
          </div>
          <div v-else class="payment-methods__text">
            {{ $t('noPaymentMethods') }}
            <PaymentMethodCardAlfabankAdd class="mt-6" v-if="account.driver === paymentDrivers.ALFABANK" />
          </div>
        </template>
        <PaymentMethodDialogAdd v-if="account.driver === paymentDrivers.STRIPE" :key="date" @created="loader(() => fetchAllAndUpdateKey())" />
      </template>
    </div>
  </AppWithLoading>
</template>

<script>
import usePaymentMethods from '@/compositions/accounting/usePaymentMethods';
import PaymentMethodsListItem from '@/components/Payment/Methods/PaymentMethodsListItem';
import AppWithLoading from '@/components/AppWithLoading';
import useAccount from '@/compositions/accounting/useAccount';
import PaymentMethodDialogAdd from '@/components/Payment/Methods/PaymentMethodDialogAdd';
import ProlongSubscription from '@/components/Payment/Subscription/ProlongSubscription';
import InactiveTariffAlert from '@/components/Payment/Subscription/InactiveTariffAlert';
import Preloader from '@/components/Preloader';
import { ref, onMounted } from '@vue/composition-api';
import useSubscription from '@/compositions/accounting/useSubscription';
import { paymentDrivers, settingsTabs, paymentTabs } from '@/enums';
import PaymentMethodCardAlfabankAdd
  from '@/components/Payment/Methods/PaymentMethodCardAlfabankAdd';

export default {
  name: 'PaymentMethodsList',
  components: {
    Preloader,
    InactiveTariffAlert,
    PaymentMethodCardAlfabankAdd,
    ProlongSubscription,
    PaymentMethodDialogAdd,
    AppWithLoading,
    PaymentMethodsListItem,
  },
  setup(props, { root: { $router } }) {
    const openedId = ref(null);
    const date = ref(Date.now());
    const renewLoading = ref(false);
    const { account } = useAccount();

    function changeOpenedId(id) {
      openedId.value = id;
    }
    const { fetchAll, paymentMethods } = usePaymentMethods();
    const { fetchIfNull } = useAccount();
    const { isExpired, hasFailedRenewal } = useSubscription();

    onMounted(() => {
      if (localStorage.getItem('redirect_on_trial_page')) {
        $router.push({
          name: 'settings',
          params: { type: settingsTabs.PAYMENT, addType: paymentTabs.TRIAL },
        });
        localStorage.removeItem('redirect_on_trial_page');
      }
    });

    function fetchData() {
      return Promise.all([fetchIfNull(), fetchAll()]);
    }

    async function fetchAllAndUpdateKey() {
      date.value = Date.now();
      await fetchData();
    }

    return {
      paymentMethods,
      fetchData,
      openedId,
      changeOpenedId,
      date,
      fetchAllAndUpdateKey,
      account,
      paymentDrivers,
      isExpired,
      hasFailedRenewal,
      renewLoading,
    };
  },
};
</script>

<style lang="scss" scoped>
  .payment-methods {
    min-height: calc(100vh - 12rem);
    padding: 2.1875rem 2.5rem 2.5rem 2.5rem;
    &__title {
      font-size: 1.5rem;
      line-height: 1.2;
      font-weight: 400;
      color: #212121;
      margin-bottom: 1.25rem;
    }
    &__text {
      font-size: 1.0625rem;
      font-weight: 400;
      color: #212121;
      margin-bottom: 2rem;
    }
  }
  @media #{map-get($display-breakpoints, 'md-and-down')} {
    .payment-methods {
      padding: 1.875rem 1rem 0.125rem;
      &__title {
        font-size: 1.25rem;
        margin-bottom: 1rem;
      }
      &__text {
        font-size: 0.9375rem;
        margin-bottom: 1.5rem;
      }
    }
  }
  @media #{map-get($display-breakpoints, 'xs-only')} {
    .payment-methods {
      padding: 1.25rem 0.625rem 0.125rem;
      &__title {
        font-size: 1.15rem;
        line-height: 1.2;
        font-weight: 500;
        margin-bottom: 0.75rem;
      }
      &__text {
        font-size: 0.8125rem;
        padding: 0 0.625rem;
        margin-bottom: 1rem;
      }
    }
  }
</style>

<i18n lang="yml" locale="ru">
  title: Сохраненные способы оплаты
  subtitle: Для добавления новой карты, мы заблокируем и разблокируем 1 рубль с вашей карты. Вы будете перенаправлены на сайт платежной системы.
  noPaymentMethods: У вас нет сохранённых способов оплаты
</i18n>
<i18n lang="yml" locale="en">
  title: Payment methods
  subtitle: To add a new card, we will block and unlock 1 ruble from your card. You will be redirected to the payment system's website.
  noPaymentMethods: Please add a payment method
</i18n>
